import { useState } from 'react';

export interface ComparisonFilters {
    searchQuery: string; // Current input value
    searchTerms: string[]; // Active filter terms
    showMatched: boolean;
    hideEmptyRooms: boolean;
    confidenceLevel: 'all' | 'high' | 'medium' | 'low';
    showOverscoped: boolean;
    showAdditional: boolean;
    showDiscrepancy: boolean;
    showNotOnInsurance: boolean;
}

export function useComparisonState() {
    const [filters, setFilters] = useState<ComparisonFilters>({
        searchQuery: '',
        searchTerms: [],
        showMatched: true,
        hideEmptyRooms: false,
        confidenceLevel: 'all',
        showOverscoped: false,
        showAdditional: false,
        showDiscrepancy: false,
        showNotOnInsurance: false,
    });

    const [selectedRoomIds, setSelectedRoomIds] = useState<Set<number>>(new Set());
    const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());

    const setSearchQuery = (query: string) => {
        setFilters(prev => ({ ...prev, searchQuery: query }));
    };

    const addSearchTerm = (termToAdd?: string) => {
        const term = termToAdd ? termToAdd.trim() : filters.searchQuery.trim();
        if (term && !filters.searchTerms.includes(term)) {
            setFilters(prev => ({
                ...prev,
                searchTerms: [...prev.searchTerms, term],
                searchQuery: '' // Clear input after adding
            }));
        }
    };

    const removeSearchTerm = (termToRemove: string) => {
        setFilters(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.filter(term => term !== termToRemove)
        }));
    };

    const clearSearchTerms = () => {
        setFilters(prev => ({ ...prev, searchTerms: [] }));
    };

    const toggleShowMatched = () => {
        setFilters(prev => ({ ...prev, showMatched: !prev.showMatched }));
    };

    const toggleHideEmptyRooms = () => {
        setFilters(prev => ({ ...prev, hideEmptyRooms: !prev.hideEmptyRooms }));
    };

    const setConfidenceLevel = (level: ComparisonFilters['confidenceLevel']) => {
        setFilters(prev => ({ ...prev, confidenceLevel: level }));
    };

    const toggleShowOverscoped = () => {
        setFilters(prev => ({ ...prev, showOverscoped: !prev.showOverscoped }));
    };

    const toggleShowAdditional = () => {
        setFilters(prev => ({ ...prev, showAdditional: !prev.showAdditional }));
    };

    const toggleShowDiscrepancy = () => {
        setFilters(prev => ({ ...prev, showDiscrepancy: !prev.showDiscrepancy }));
    };

    const toggleShowNotOnInsurance = () => {
        setFilters(prev => ({ ...prev, showNotOnInsurance: !prev.showNotOnInsurance }));
    };

    const applySingleFilter = (filter: 'overscoped' | 'additional' | 'discrepancy' | 'notOnInsurance') => {
        setFilters(prev => ({
            ...prev,
            showOverscoped: filter === 'overscoped',
            showAdditional: filter === 'additional',
            showDiscrepancy: filter === 'discrepancy',
            showNotOnInsurance: filter === 'notOnInsurance',
            // Reset other view filters
            showMatched: true, // Maybe keep matched? The user said "remove any other filters that were applied".
            hideEmptyRooms: false,
            searchQuery: '',
            searchTerms: []
        }));
    };

    const setSpecialFilter = (filter: 'overscoped' | 'additional' | 'discrepancy' | 'notOnInsurance' | 'none') => {
        setFilters(prev => ({
            ...prev,
            showOverscoped: filter === 'overscoped',
            showAdditional: filter === 'additional',
            showDiscrepancy: filter === 'discrepancy',
            showNotOnInsurance: filter === 'notOnInsurance',
        }));
    };

    const toggleRoomSelection = (roomId: number) => {
        const newSelected = new Set(selectedRoomIds);
        if (newSelected.has(roomId)) {
            newSelected.delete(roomId);
        } else {
            newSelected.add(roomId);
        }
        setSelectedRoomIds(newSelected);
    };

    const toggleItemSelection = (itemId: number) => {
        const newSelected = new Set(selectedItemIds);
        if (newSelected.has(itemId)) {
            newSelected.delete(itemId);
        } else {
            newSelected.add(itemId);
        }
        setSelectedItemIds(newSelected);
    };

    const setItemsSelection = (itemIds: number[], isSelected: boolean) => {
        const newSelected = new Set(selectedItemIds);
        itemIds.forEach(id => {
            if (isSelected) newSelected.add(id);
            else newSelected.delete(id);
        });
        setSelectedItemIds(newSelected);
    };

    const clearSelection = () => {
        setSelectedRoomIds(new Set());
        setSelectedItemIds(new Set());
    };

    return {
        filters,
        setSearchQuery,
        addSearchTerm,
        removeSearchTerm,
        clearSearchTerms,
        toggleShowMatched,
        toggleHideEmptyRooms,
        setConfidenceLevel,
        toggleShowOverscoped,
        toggleShowAdditional,
        toggleShowDiscrepancy,
        toggleShowNotOnInsurance,
        applySingleFilter,
        setSpecialFilter,
        selection: {
            selectedRoomIds,
            selectedItemIds,
            toggleRoomSelection,
            toggleItemSelection,
            setItemsSelection,
            clearSelection
        }
    };
}
